import { fetchGeoData } from '../../api';
import { type GooglePlacesAddress } from '../types';

/**
 * Extracts a google places address from a google places id.
 */
export const fetchGooglePlacesAddress = async (
  props: FetchGooglePlacesParams,
  cityKeys = defaultCityKeys,
): Promise<GooglePlacesAddress> => {
  const response = await fetchGeoData(props);

  const findAddressComponent = (component: string) => {
    const match = response.results[0]?.address_components.find(
      (e: Readonly<{ types: readonly string[] }>) =>
        e.types.includes(component),
    );

    return match;
  };

  const cityKey = cityKeys.find(findAddressComponent) ?? 'locality';

  const place = response?.results?.[0] ?? {};
  const { place_id: googlePlaceId, geometry } = place;

  const streetLongName = findAddressComponent('street_number')?.long_name ?? '';
  const routeLongName = findAddressComponent('route')?.long_name ?? '';
  const street = [streetLongName, routeLongName].join(' ').trim();
  const zipCode = findAddressComponent('postal_code')?.long_name ?? '';
  const city = findAddressComponent(cityKey)?.long_name ?? '';
  const state = findAddressComponent('administrative_area_level_1')?.short_name ?? ''; // prettier-ignore
  const country = findAddressComponent('country')?.long_name ?? '';
  const { lat = 0, lng = 0 } = geometry?.location ?? {};
  const latitude = typeof lat === 'function' ? 0 : lat;
  const longitude = typeof lng === 'function' ? 0 : lng;
  const formattedAddress = response.results[0]?.formatted_address ?? '';

  return {
    googlePlaceId,
    latitude,
    longitude,
    street,
    zipCode,
    city,
    state,
    country,
    formattedAddress,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const defaultCityKeys = [
  'locality',
  'sublocality',
  'sublocality_level_1',
  'sublocality_level_2',
  'sublocality_level_3',
  'sublocality_level_4',
  'sublocality_level_5',
  'administrative_area_level_3',
  'administrative_area_level_2',
  'neighborhood',
];

// ──── Types ──────────────────────────────────────────────────

type FetchGooglePlacesParams = {
  placeId: string;
  placeAddress?: string;
  apiKey: string;
};
